if (!customElements.get("m-what-to-expect")) {
  class MWhatToExpect extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.rafId = null;
      this.fadeTimer = null;
      this.fadeToken = 0;
      this.copyFadeMs = 220;
      this.values = [];
      this.labels = [];
      this.onTabClick = this.onTabClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll("[data-wte-tab]"));
      this.panels = Array.from(this.querySelectorAll("[data-wte-panel]"));
      this.line = this.querySelector("[data-wte-line]");
      this.dot = this.querySelector("[data-wte-dot]");
      this.xLabels = this.querySelector("[data-wte-x-labels]");
      this.duration = Number(this.dataset.duration) || 480;

      this.values = this.parseNumberList(this.dataset.chartValues);
      this.labels = this.parseLabelList(this.dataset.chartLabels);

      if (!this.values.length && this.tabs.length) {
        this.values = this.tabs.map((_, index) => {
          const step = this.tabs.length > 1 ? index / (this.tabs.length - 1) : 0;
          return Math.round(12 + step * 83);
        });
      }

      this.renderXLabels();
      this.bindEvents();

      const initial = this.tabs.findIndex((tab) => tab.classList.contains("is-active"));
      this.activeIndex = initial >= 0 ? initial : 0;
      this.setWeek(this.activeIndex, true);

      if (window.Shopify && Shopify.designMode) {
        this._onSectionLoad = (event) => {
          if (event.target && event.target.contains(this)) {
            this.setWeek(this.activeIndex, true);
          }
        };
        document.addEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.fadeTimer) window.clearTimeout(this.fadeTimer);
      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.onTabClick);
        tab.removeEventListener("keydown", this.onKeyDown);
      });
      if (this._onSectionLoad) {
        document.removeEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    bindEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.onTabClick);
        tab.addEventListener("keydown", this.onKeyDown);
      });
    }

    parseNumberList(value) {
      if (!value) return [];
      return String(value)
        .split(",")
        .map((item) => Number(item.trim()))
        .filter((item) => !Number.isNaN(item));
    }

    parseLabelList(value) {
      if (!value) return [];
      return String(value)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    onTabClick(event) {
      const tab = event.currentTarget;
      const index = Number(tab.dataset.weekIndex);
      if (Number.isNaN(index) || index === this.activeIndex) return;
      this.setWeek(index, false);
    }

    onKeyDown(event) {
      if (!["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        return;
      }

      event.preventDefault();
      let next = this.activeIndex;

      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        next = Math.min(this.activeIndex + 1, this.tabs.length - 1);
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        next = Math.max(this.activeIndex - 1, 0);
      } else if (event.key === "Home") {
        next = 0;
      } else if (event.key === "End") {
        next = this.tabs.length - 1;
      }

      if (next !== this.activeIndex) {
        this.setWeek(next, false);
        this.tabs[next].focus();
      }
    }

    setWeek(index, instant) {
      if (!this.tabs.length) return;

      const nextIndex = Math.max(0, Math.min(index, this.tabs.length - 1));
      const previousIndex = this.activeIndex;
      this.activeIndex = nextIndex;

      this.tabs.forEach((tab, i) => {
        const isActive = i === nextIndex;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.tabIndex = isActive ? 0 : -1;
      });

      const reduceMotion = instant || this.prefersReducedMotion();
      this.animatePanels(previousIndex, nextIndex, reduceMotion);
      this.animateGraph(previousIndex, nextIndex, reduceMotion);
    }

    applyPanelState(nextIndex, { fadingIn = false } = {}) {
      this.panels.forEach((panel, i) => {
        const isActive = i === nextIndex;
        panel.classList.toggle("is-active", isActive);
        panel.classList.toggle("is-fading", isActive && fadingIn);
        if (isActive) {
          panel.removeAttribute("hidden");
        } else {
          panel.setAttribute("hidden", "");
          panel.classList.remove("is-fading");
        }
      });
    }

    animatePanels(previousIndex, nextIndex, instant) {
      if (this.fadeTimer) {
        window.clearTimeout(this.fadeTimer);
        this.fadeTimer = null;
      }

      this.fadeToken += 1;
      const token = this.fadeToken;

      if (instant || previousIndex === nextIndex || !this.panels.length) {
        this.applyPanelState(nextIndex);
        return;
      }

      const prevPanel = this.panels[previousIndex];
      if (prevPanel) prevPanel.classList.add("is-fading");

      this.fadeTimer = window.setTimeout(() => {
        if (token !== this.fadeToken) return;

        this.applyPanelState(nextIndex, { fadingIn: true });

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (token !== this.fadeToken) return;
            const nextPanel = this.panels[nextIndex];
            if (nextPanel) nextPanel.classList.remove("is-fading");
          });
        });

        this.fadeTimer = null;
      }, this.copyFadeMs);
    }

    renderXLabels() {
      if (!this.xLabels) return;
      const labels =
        this.labels.length >= this.values.length
          ? this.labels
          : this.values.map((_, index) => String(index + 1));

      this.xLabels.innerHTML = labels
        .slice(0, Math.max(this.values.length, labels.length))
        .map((label) => `<span class="m-what-to-expect__x-label">${this.escapeHtml(label)}</span>`)
        .join("");
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    /** Map chart value 0–100 to SVG Y (LOW at bottom, HIGH at top). */
    valueToY(value) {
      const clamped = Math.max(0, Math.min(100, Number(value) || 0));
      const top = 20;
      const bottom = 100;
      return bottom - (clamped / 100) * (bottom - top);
    }

    pointX(index, count) {
      if (count <= 1) return 8;
      const left = 8;
      const right = 632;
      return Math.round((left + (index / (count - 1)) * (right - left)) * 10) / 10;
    }

    easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    /**
     * Build polyline points for a fractional end index along the week scale.
     * e.g. endProgress 2.4 = points 0..2 fully, plus a tip 40% toward point 3.
     * This draws left-to-right along the path instead of bouncing Y values.
     */
    pointsForProgress(endProgress) {
      const total = Math.max(this.values.length, 1);
      const maxIndex = this.values.length - 1;
      const clamped = Math.max(0, Math.min(endProgress, maxIndex));
      const whole = Math.floor(clamped);
      const frac = clamped - whole;
      const coords = [];

      for (let i = 0; i <= whole; i++) {
        coords.push({
          x: this.pointX(i, total),
          y: this.valueToY(this.values[i]),
        });
      }

      if (frac > 0 && whole < maxIndex) {
        const x0 = this.pointX(whole, total);
        const y0 = this.valueToY(this.values[whole]);
        const x1 = this.pointX(whole + 1, total);
        const y1 = this.valueToY(this.values[whole + 1]);
        coords.push({
          x: Math.round((x0 + (x1 - x0) * frac) * 10) / 10,
          y: Math.round((y0 + (y1 - y0) * frac) * 10) / 10,
        });
      }

      return coords;
    }

    applyCoords(coords) {
      if (!this.line || !this.dot || !coords.length) return;

      this.line.setAttribute(
        "points",
        coords.map((point) => `${point.x},${point.y}`).join(" ")
      );

      // Position HTML tip as % of the plot canvas so it stays a true circle
      // even when the SVG is stretched with preserveAspectRatio="none".
      const tip = coords[coords.length - 1];
      const leftPct = (tip.x / 640) * 100;
      const topPct = (tip.y / 140) * 100;
      this.dot.style.left = `${leftPct}%`;
      this.dot.style.top = `${topPct}%`;
    }

    animateGraph(fromIndex, toIndex, instant) {
      if (!this.line || !this.dot || !this.values.length) return;

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      const startProgress = Math.max(0, Math.min(fromIndex, this.values.length - 1));
      const endProgress = Math.max(0, Math.min(toIndex, this.values.length - 1));

      if (instant || startProgress === endProgress) {
        this.applyCoords(this.pointsForProgress(endProgress));
        return;
      }

      const duration = this.duration;
      const start = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = this.easeOutCubic(progress);
        const current = startProgress + (endProgress - startProgress) * eased;
        this.applyCoords(this.pointsForProgress(current));

        if (progress < 1) {
          this.rafId = requestAnimationFrame(tick);
        } else {
          this.applyCoords(this.pointsForProgress(endProgress));
          this.rafId = null;
        }
      };

      this.rafId = requestAnimationFrame(tick);
    }
  }

  customElements.define("m-what-to-expect", MWhatToExpect);
}
